import { useClientsQuery } from "@graphql/clients/clients.g";
import {
  ComponentProps,
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { generatePath, matchPath, useNavigate, useParams } from "react-router";

import { SelectClientOption } from "@components/custom-select-components/options/SelectClientOption/SelectClientOption";
import { SelectClientValue } from "@components/custom-select-components/values/SelectClientValue/SelectClientValue";

import { Select } from "@design-system/Select/Select";

import { routes } from "@pages/routes";

/*
 * When switching between clients, we want to persist the location for these routes.
 * Otherwise, the location will be reset to the dashboard.
 */
const PERSISTED_ROUTES = [
  routes.portal.clients.client.clientEmployees.$path(),
  routes.portal.clients.client.applications.$path(),
  routes.portal.clients.client.dashboard.$path(),
  routes.portal.clients.client.record.$path(),
] as const;

type Props = {
  onChange?: ComponentProps<typeof Select>["onChange"];
};

export const CurrentClientSelect = ({ onChange }: Props) => {
  const navigate = useNavigate();

  const { clientId } = useParams();

  const clientsQuery = useClientsQuery();

  // Track the display value (what's shown in the select)
  // When dropdown opens, this will be cleared to ""
  // When dropdown closes without selection, it reverts to originalClientId
  const [displayValue, setDisplayValue] = useState(clientId ?? "");

  // Track the original value to revert to if no selection is made
  const originalClientIdRef = useRef(clientId ?? "");

  // Sync displayValue and originalClientIdRef when clientId changes from URL/props
  useEffect(() => {
    if (clientId && clientId !== originalClientIdRef.current) {
      originalClientIdRef.current = clientId;
      setDisplayValue(clientId);
    }
  }, [clientId]);

  const clients = useMemo(() => {
    return clientsQuery.data?.clients?.edges
      .map((client) => client?.node)
      .filter((client) => !!client);
  }, [clientsQuery.data]);

  const options = useMemo(
    () =>
      clients?.map((client) => ({
        id: client.id,
        label: client.name,
      })),
    [clients],
  );

  const handleOpenChange = useCallback((open: boolean) => {
    if (open) {
      // When opening, clear the display value to show all options
      setDisplayValue("");
    } else {
      // When closing, revert to original value if still empty
      setDisplayValue((current) => {
        if (!current || current === "") {
          return originalClientIdRef.current;
        }
        return current;
      });
    }
  }, []);

  const handleChange: ComponentProps<typeof Select>["onChange"] = (id) => {
    // Only navigate when a valid client is selected from the dropdown
    if (!id || id === "") {
      return;
    }

    // Update both display value and original value
    setDisplayValue(id as string);
    originalClientIdRef.current = id as string;

    // Only navigate if the selected client is different from URL clientId
    if (id === clientId) {
      return;
    }

    const persistedRoute = PERSISTED_ROUTES.find((route) =>
      matchPath(route, location.pathname),
    );

    if (persistedRoute) {
      const path = generatePath(persistedRoute, {
        clientId: id as string,
      });

      navigate(path);
    } else {
      navigate(
        routes.portal.clients.client.dashboard.$buildPath({
          params: { clientId: id as string },
        }),
      );
    }

    onChange?.(id);
  };

  if (!clientId) {
    return null;
  }

  return (
    <Select
      value={displayValue}
      options={options}
      onChange={handleChange}
      onOpenChange={handleOpenChange}
      OptionComponent={SelectClientOption}
      ValueComponent={SelectClientValue}
    />
  );
};
